<template>
  <div class="school-subjects-management">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">
            <v-icon size="28" class="mr-3">mdi-book-open-variant</v-icon>
            Gestión de Asignaturas
          </h1>
          <p class="page-subtitle">Administra las asignaturas escolares del sistema</p>
        </div>

        <div class="header-actions">
          <v-btn
            @click="openCreateDialog"
            color="primary"
            size="large"
            class="create-btn"
            prepend-icon="mdi-plus-circle"
          >
            Crear Asignatura
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="stats-section mb-6">
      <v-row>
        <v-col cols="12" sm="6" md="4">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="text-center">
              <v-icon size="32" color="primary" class="mb-2">mdi-book-multiple</v-icon>
              <div class="stats-number">{{ totalSubjects }}</div>
              <div class="stats-label">Total de Asignaturas</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="text-center">
              <v-icon size="32" color="success" class="mb-2">mdi-check-circle</v-icon>
              <div class="stats-number">{{ activeSubjects }}</div>
              <div class="stats-label">Asignaturas Activas</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="text-center">
              <v-icon size="32" color="error" class="mb-2">mdi-cancel</v-icon>
              <div class="stats-number">{{ inactiveSubjects }}</div>
              <div class="stats-label">Asignaturas Inactivas</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Subjects Table -->
    <v-card class="subjects-table-card" elevation="2">
      <v-card-title class="table-header">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Buscar asignatura..."
          single-line
          hide-details
          clearable
          density="comfortable"
          class="search-field"
        ></v-text-field>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="filteredSubjects"
        :loading="loading"
        :items-per-page="itemsPerPage"
        :page="currentPage"
        class="subjects-table"
        @update:page="currentPage = $event"
        @update:items-per-page="itemsPerPage = $event"
      >
        <template #item.is_active="{ item }">
          <v-chip
            :color="item.is_active ? 'success' : 'error'"
            size="small"
            class="status-chip"
          >
            {{ item.is_active ? 'Activa' : 'Inactiva' }}
          </v-chip>
        </template>

        <template #item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template #item.actions="{ item }">
          <div class="action-buttons">
            <v-btn
              icon
              size="small"
              variant="text"
              @click="openEditDialog(item)"
              color="primary"
            >
              <v-icon>mdi-pencil</v-icon>
              <v-tooltip activator="parent" location="top">Editar</v-tooltip>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              @click="openDeleteDialog(item)"
              color="error"
            >
              <v-icon>mdi-delete</v-icon>
              <v-tooltip activator="parent" location="top">Eliminar</v-tooltip>
            </v-btn>
          </div>
        </template>

        <template #loading>
          <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
        </template>

        <template #no-data>
          <div class="text-center py-8">
            <v-icon size="64" color="grey-lighten-1">mdi-book-off</v-icon>
            <p class="text-h6 text-grey mt-4">No hay asignaturas disponibles</p>
            <v-btn @click="openCreateDialog" color="primary" class="mt-4">
              Crear primera asignatura
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Subject Form Dialog -->
    <v-dialog v-model="showFormDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="dialog-header">
          <span class="text-h5">
            <v-icon class="mr-2">mdi-book-open-variant</v-icon>
            {{ isEditing ? 'Editar Asignatura' : 'Nueva Asignatura' }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeFormDialog" :disabled="formLoading">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="formRef" v-model="formValid">
            <v-text-field
              v-model="formData.name"
              label="Nombre de la asignatura"
              :rules="[rules.required, rules.maxLength(100)]"
              prepend-inner-icon="mdi-book-open-variant"
              variant="outlined"
              :disabled="formLoading"
              class="mb-4"
            ></v-text-field>

            <v-switch
              v-model="formData.is_active"
              label="Asignatura activa"
              color="primary"
              :disabled="formLoading"
              hide-details
            ></v-switch>
          </v-form>

          <v-alert v-if="formError" type="error" class="mt-4">
            {{ formError }}
          </v-alert>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer></v-spacer>
          <v-btn @click="closeFormDialog" :disabled="formLoading" variant="outlined">
            Cancelar
          </v-btn>
          <v-btn
            @click="handleSubmit"
            color="primary"
            :loading="formLoading"
            :disabled="!formValid"
          >
            {{ isEditing ? 'Actualizar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <ConfirmDialog
      v-model:dialog="showDeleteDialog"
      title="Confirmar Eliminación"
      :message="`¿Estás seguro de que deseas eliminar la asignatura ${selectedSubject?.name}? Esta acción no se puede deshacer.`"
      confirm-text="Eliminar"
      confirm-color="error"
      :loading="deleteLoading"
      @confirm="handleDelete"
      @cancel="closeDeleteDialog"
    />

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="4000"
      location="top right"
      class="custom-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">
          {{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
        </v-icon>
        {{ snackbarMessage }}
      </div>
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import ConfirmDialog from '@/components/admin/ConfirmDialog.vue'

const store = useStore()

// Data
const search = ref('')
const currentPage = ref(1)
const itemsPerPage = ref(10)
const showFormDialog = ref(false)
const showDeleteDialog = ref(false)
const selectedSubject = ref(null)
const formValid = ref(false)
const formLoading = ref(false)
const deleteLoading = ref(false)
const formError = ref('')
const formRef = ref(null)

const showSnackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

const formData = ref({
  name: '',
  is_active: true
})

// Computed
const subjects = computed(() => store.getters['schoolSubjects/allSubjects'])
const loading = computed(() => store.getters['schoolSubjects/isLoading'])
const isEditing = computed(() => !!selectedSubject.value)

const totalSubjects = computed(() => subjects.value.length)
const activeSubjects = computed(() => subjects.value.filter(s => s.is_active).length)
const inactiveSubjects = computed(() => subjects.value.filter(s => !s.is_active).length)

const filteredSubjects = computed(() => {
  if (!search.value) return subjects.value

  const searchLower = search.value.toLowerCase()
  return subjects.value.filter(subject =>
    subject.name.toLowerCase().includes(searchLower)
  )
})

// Table headers
const headers = [
  { title: 'ID', key: 'id', align: 'start', sortable: true },
  { title: 'Nombre', key: 'name', align: 'start', sortable: true },
  { title: 'Estado', key: 'is_active', align: 'center', sortable: true },
  { title: 'Fecha de Creación', key: 'created_at', align: 'start', sortable: true },
  { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
]

// Validation rules
const rules = {
  required: value => !!value || 'Este campo es requerido',
  maxLength: (max) => value => !value || value.length <= max || `Máximo ${max} caracteres`
}

// Methods
onMounted(async () => {
  await loadSubjects()
})

const loadSubjects = async () => {
  await store.dispatch('schoolSubjects/fetchAllSubjects')
}

const openCreateDialog = () => {
  selectedSubject.value = null
  formData.value = {
    name: '',
    is_active: true
  }
  formError.value = ''
  showFormDialog.value = true
}

const openEditDialog = (subject) => {
  selectedSubject.value = subject
  formData.value = {
    name: subject.name,
    is_active: subject.is_active
  }
  formError.value = ''
  showFormDialog.value = true
}

const closeFormDialog = () => {
  showFormDialog.value = false
  selectedSubject.value = null
  formData.value = {
    name: '',
    is_active: true
  }
  formError.value = ''
  if (formRef.value) {
    formRef.value.reset()
  }
}

const handleSubmit = async () => {
  if (!formRef.value) return

  const { valid } = await formRef.value.validate()
  if (!valid) return

  formLoading.value = true
  formError.value = ''

  try {
    let result

    if (isEditing.value) {
      result = await store.dispatch('schoolSubjects/updateSubject', {
        id: selectedSubject.value.id,
        subjectData: formData.value
      })
    } else {
      result = await store.dispatch('schoolSubjects/createSubject', formData.value)
    }

    if (result.success) {
      closeFormDialog()
      await loadSubjects()
      showSuccessMessage(result.message || `Asignatura ${isEditing.value ? 'actualizada' : 'creada'} exitosamente`)
    } else {
      formError.value = result.error || 'Error al guardar la asignatura'
    }
  } catch (error) {
    formError.value = 'Ha ocurrido un error inesperado'
  } finally {
    formLoading.value = false
  }
}

const openDeleteDialog = (subject) => {
  selectedSubject.value = subject
  showDeleteDialog.value = true
}

const closeDeleteDialog = () => {
  showDeleteDialog.value = false
  selectedSubject.value = null
}

const handleDelete = async () => {
  if (!selectedSubject.value) return

  deleteLoading.value = true

  try {
    const result = await store.dispatch('schoolSubjects/deleteSubject', selectedSubject.value.id)

    if (result.success) {
      closeDeleteDialog()
      await loadSubjects()
      showSuccessMessage('Asignatura eliminada exitosamente')
    } else {
      showErrorMessage(result.error || 'Error al eliminar la asignatura')
    }
  } catch (error) {
    showErrorMessage('Ha ocurrido un error inesperado')
  } finally {
    deleteLoading.value = false
  }
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const showSuccessMessage = (message) => {
  snackbarMessage.value = message
  snackbarColor.value = 'success'
  showSnackbar.value = true
}

const showErrorMessage = (message) => {
  snackbarMessage.value = message
  snackbarColor.value = 'error'
  showSnackbar.value = true
}
</script>

<style scoped>
.school-subjects-management {
  padding: 24px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.page-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  color: #0a192f;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.page-subtitle {
  color: #64748b;
  font-size: 1.1rem;
  margin: 0;
}

.create-btn {
  background: linear-gradient(135deg, #0a192f 0%, #112240 100%) !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
  border-radius: 12px;
  padding: 0 24px;
  height: 48px;
  box-shadow: 0 4px 12px rgba(10, 25, 47, 0.3);
  transition: all 0.2s ease;
}

.create-btn:hover {
  box-shadow: 0 6px 16px rgba(10, 25, 47, 0.4);
  transform: translateY(-2px);
}

.stats-section {
  margin-bottom: 24px;
}

.stats-card {
  border-radius: 16px !important;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.stats-card:hover {
  box-shadow: 0 8px 24px rgba(10, 25, 47, 0.15) !important;
  transform: translateY(-2px);
}

.stats-number {
  font-size: 2rem;
  font-weight: 700;
  color: #0a192f;
  margin-bottom: 4px;
}

.stats-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.subjects-table-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0;
}

.table-header {
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 20px 24px;
}

.search-field {
  max-width: 400px;
}

.subjects-table {
  background: #ffffff;
}

.status-chip {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
}

.action-buttons {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.dialog-header {
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.custom-snackbar {
  border-radius: 12px;
}

.custom-snackbar :deep(.v-snackbar__wrapper) {
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

@media (max-width: 768px) {
  .school-subjects-management {
    padding: 16px;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .page-title {
    font-size: 1.75rem;
  }

  .create-btn {
    width: 100%;
    justify-content: center;
  }

  .search-field {
    max-width: 100%;
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 1.5rem;
  }

  .page-subtitle {
    font-size: 1rem;
  }
}
</style>
